import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const HeroSection = () => {
  const { t } = useTranslation();
  const [scrollY, setScrollY] = useState(0);
  const [backgroundUrl, setBackgroundUrl] = useState('/images/HeroSection.webp');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });

    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);

    const img = new Image();
    img.src = backgroundUrl;
    img.onerror = () => {
      console.warn('Error loading HeroSection.webp, using fallback.');
      setBackgroundUrl('/images/fallback.png');
    };

    return () => window.removeEventListener('scroll', handleScroll);
  }, [backgroundUrl]);

  const translateY = Math.min(scrollY * 0.4, 200);
  const scale = 1 + Math.min(scrollY / 2000, 0.1);
  const blurAmount = Math.min(scrollY / 10, 20);

  return (
    <section className="relative w-full min-h-screen overflow-hidden bg-black">
      <motion.div
        className="absolute inset-0 bg-center bg-cover will-change-transform"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5, ease: 'easeOut' }}
        style={{
          backgroundImage: `url('${backgroundUrl}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: '#111111',
          transform: `translateY(${translateY}px) scale(${scale})`,
          filter: `blur(${blurAmount}px)`
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40" />
      </motion.div>

      <div className="relative container-custom min-h-screen flex flex-col justify-end pb-24 sm:pb-32 z-10">
        <motion.div
          className={`max-w-xl transition-[backdrop-filter] duration-300 ${scrollY > 10 ? 'backdrop-blur-sm' : ''}`}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.4, ease: 'easeOut' }}
        >
          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl text-white font-medium mb-6 leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.6 }}
          >
            {t('home.hero.title')}
          </motion.h1>

          <motion.p
            className="text-white/90 text-lg mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.8 }}
          >
            {t('home.hero.subtitle')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 1 }}
          >
            <Link
              to="/projects"
              className="inline-flex items-center px-6 py-3 transition-colors group text-white bg-[#5F5F5F] hover:bg-[#C8102E]"
            >
              {t('home.hero.cta')}
              <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" size={18} />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
